import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../../Components/ui/dialog';
import { Button } from '../../Components/ui/button';
import { Input } from '../../Components/ui/input';
import { Plus, SkipForward, XCircle } from 'lucide-react';
import { FaTrash, FaFilePdf } from 'react-icons/fa';
import Default_WhatsApp_background from '../../Assets/Default_WhatsApp_background.jpeg';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/State/store';
import axios from 'axios';
import { toast } from 'react-toastify';

interface TestModeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: number | null;
}

interface TemplateComponent {
  type: string;
  format?: string;
  text?: string;
  buttons?: {
    type: string;
    text: string;
    url?: string;
    phone_number?: string;
  }[];
}

interface ParsedTemplate {
  header?: TemplateComponent;
  body?: TemplateComponent;
  footer?: TemplateComponent;
  buttons?: TemplateComponent;
}

interface TemplateDetails {
  operator_mapping_id: number;
  campaign_id: number;
  operator_id: number;
  channel_id: number;
  channel_config_id: number;
  template_id: number;
  user_account_id: number;
  translated_template: string;
  channelserver_url: string;
}

interface ResultItem {
  msisdn: string;
  status: string;
  used_msg_count?: number;
  remarks?: string;
}

interface SendResult {
  status: 'success' | 'partial';
  total: number;
  success_count: number;
  failure_count: number;
  skipped_count: number;
  results: ResultItem[];
}

const MAX_CONTACTS = 10;

const renderFormattedText = (text: string) => {
  return text
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<s>$1</s>')
    .replace(
      /`([^`]+)`/g,
      '<code style="background:#f1f1f1;padding:2px 4px;border-radius:3px;">$1</code>',
    )
    .replace(
      /\{\{(\d+)\}\}/g,
      '<span style="background:#e3f2fd;padding:2px 4px;border-radius:3px;color:#1976d2;">{{$1}}</span>',
    );
};

const TestModeDialog: React.FC<TestModeDialogProps> = ({
  open,
  onOpenChange,
  campaignId,
}) => {
  const [contacts, setContacts] = useState<string[]>(['']);
  const [parsedTemplate, setParsedTemplate] = useState<ParsedTemplate | null>(
    null,
  );
    const [base64, setBase64] = useState('');
type MediaConfig = {
  file_name?: string;
  file_type?: string;
  file_length?: string;
  binary_data?: string;
};

const [mediaConfig, setMediaConfig] = useState<MediaConfig | null>(null);
  const [templateDetails, setTemplateDetails] =
    useState<TemplateDetails | null>(null);
  const [rawMessage, setRawMessage] = useState<object | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [view, setView] = useState<'form' | 'result'>('form');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL,
  );
    const apiUrlAdminAcc = useSelector(
      (state: RootState) => state.authentication.adminUrl
    );
  const workspaceId = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );

  const accountId = useSelector(
    (state: RootState) => state.authentication.account_id,
  );
  const QuickTestUrl = useSelector((state: RootState) => state.authentication.QuickTestUrl);

  const isImpersonated = useSelector(
    (state: RootState) => state.admin.Impersonator?.ImpersonationState,
  );
  const impersonatorAID = useSelector(
    (state: RootState) => state.admin.Impersonator?.ImpersonatorAID,
  );


  console.log("myaccountId", impersonatorAID)
  console.log("myaccountId", isImpersonated)
  // ── Fetch & parse template whenever the dialog opens ──────────────────────
  useEffect(() => {
    if (!open || !campaignId) return;

    const fetchTemplate = async () => {
      setLoadingPreview(true);
      setParsedTemplate(null);
      setTemplateDetails(null);
      setRawMessage(null);

      try {
        const res = await axios.get(
          `${apiUrlAdvAcc}/GetFirstApprovedWhatsAppTemplate`,
          { params: { campaignId } },
        );

        if (res.data?.status === 'Failure') {
          toast.error(
            res.data?.status_Description ??
            'Test mode is currently unavailable',
          );
          onOpenChange(false);
          return;
        }

        if (
          res.data?.status === 'Success' &&
          res.data?.template_details?.translated_template
        ) {
          const details: TemplateDetails = res.data.template_details;
          setTemplateDetails(details);

          const raw = JSON.parse(details.translated_template);

          // ✅ Find HEADER component
const headerComponent = raw?.template?.components?.find(
  (comp: any) => comp.type?.toUpperCase() === "HEADER"
);

setMediaConfig(raw?.template?.media_config)

// ✅ Normalize format (case-insensitive)
const headerFormat = (headerComponent?.format || "").toUpperCase();

// ✅ Remove binary_data if IMAGE / VIDEO / DOCUMENT
if (
  ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
  raw?.media_config?.binary_data
) {
  setBase64(raw?.media_config?.binary_data)
}


          setRawMessage(raw);

          const components: TemplateComponent[] =
            raw?.template?.components ?? [];
          const result: ParsedTemplate = {};
          for (const comp of components) {
            const t = comp.type?.toUpperCase();
            if (t === 'HEADER') result.header = comp;
            if (t === 'BODY') result.body = comp;
            if (t === 'FOOTER') result.footer = comp;
            if (t === 'BUTTONS') result.buttons = comp;
          }
          setParsedTemplate(result);
        }
      } catch (err) {
        console.error('Failed to fetch template preview:', err);
        toast.error('Failed to load template preview.');
      } finally {
        setLoadingPreview(false);
      }
    };

    fetchTemplate();
  }, [open, campaignId]);

    const getBase64Src = (base64: string, type: string) => {
    if (!base64) return null;

    // If base64 already has data URI prefix, return as is
    if (base64.startsWith('data:')) {
      return base64;
    }

    // Otherwise, add the appropriate mime type prefix
    let mimeType = '';
    if (type === 'image') {
      mimeType = headerComp?.type || 'image/jpeg';
    } else if (type === 'video') {
      mimeType = headerComp?.type || 'video/mp4';
    } else if (type === 'document') {
      mimeType = 'application/pdf';
    }

    return `data:${mimeType};base64,${base64}`;
  };

  const noContentSelected =
    !loadingPreview &&
    (!parsedTemplate ||
      (!parsedTemplate.header &&
        !parsedTemplate.body &&
        !parsedTemplate.footer &&
        !parsedTemplate.buttons));

  // ── Validation ─────────────────────────────────────────────────────────────
  const isValidPhoneInput = (value: string) => /^\+?\d*$/.test(value);

  // ── Contact handlers ───────────────────────────────────────────────────────
  const handleChange = (index: number, value: string) => {
    if (!isValidPhoneInput(value)) return;

    const isDuplicate = contacts.some(
      (c, i) => i !== index && c.trim() === value.trim() && value.trim() !== '',
    );

    if (isDuplicate) {
      toast.error('This phone number has already been added.');
      return;
    }

    setContacts((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddField = () => {
    if (contacts.length < MAX_CONTACTS) {
      setContacts((prev) => [...prev, '']);
    }
  };

  const handleRemove = (index: number) => {
    if (contacts.length === 1) return;
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const sanitizeMsisdn = (phone: string) =>
    phone.replace(/[\+\s\-]/g, '').trim();

  // ── Send handler ───────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!templateDetails || !rawMessage) {
      toast.error('Template details not loaded.');
      return;
    }

    const msisdn = contacts.filter((c) => c.trim()).map(sanitizeMsisdn);

    if (msisdn.length === 0) {
      toast.error('Please enter at least one contact.');
      return;
    }

    // ✅ Deep clone rawMessage (important to avoid mutating original state)
const processedMessage = JSON.parse(JSON.stringify(rawMessage));

// ✅ Find HEADER component
const headerComponent = processedMessage?.template?.components?.find(
  (comp: any) => comp.type?.toUpperCase() === "HEADER"
);

// ✅ Get format (case-insensitive safe)
const headerFormat = (headerComponent?.format || "").toUpperCase();

// ✅ Remove binary_data if IMAGE / VIDEO / DOCUMENT
if (
  ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
  processedMessage?.media_config?.binary_data
) {
  delete processedMessage.media_config.binary_data;
}

    const payload = {
      type: 'test_mode',
      account_id: templateDetails.user_account_id,
      channel_config_id: templateDetails.channel_config_id,
      operator_id: templateDetails.operator_id,
      operator_mapping_id: templateDetails.operator_mapping_id,
      campaign_id: templateDetails.campaign_id,
      channel_id: templateDetails.channel_id,
      template_id: templateDetails.template_id,
      msisdn,
      channelserver_url: templateDetails.channelserver_url,
      message: processedMessage
    };

    try {
      setIsSending(true);

      const res = await axios.post(`${QuickTestUrl}/api/send-message`,
        payload,
      );
      const data = res.data;

      await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
        UserAccountId: accountId,
        CampaignId: campaignId,
        AdvertiserWorkspaceId: workspaceId,
        Action: 'Test Mode Message',
        ActionPage: 'Test Campaign',
        Status: 'Success',
        BeforeData: null,
        AfterData: JSON.stringify(payload),
        Comments: "Test message initiated"

      });

if (data.status === 'error') {
  const totalTested = (data.failure_count ?? 0) + (data.skipped_count ?? 0) + (data.success_count ?? 0);

  await Promise.all([
    axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: 'Test Mode Message',
      ActionPage: 'Test Campaign',
      Status: 'Failed',
      BeforeData: JSON.stringify(payload),
      AfterData: JSON.stringify(data),
      Comments: "Test message failed to send",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),

    axios.post(`${apiUrlAdminAcc}/InsertAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: `${totalTested} contacts tested successfully`,
      ActionPage: 'Test Campaign',
      Status: 'active',
      BeforeData: "",
      AfterData: "",
      Comments: "Test message failed to send",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),
  ]);

  toast.error(data.message ?? 'Failed to send test message.');
  return;
}

if (data.status === 'success' || data.status === 'partial') {
  setSendResult(data);
  setContacts(['']);
  setView('result');

  const totalTested = (data.failure_count ?? 0) + (data.skipped_count ?? 0) + (data.success_count ?? 0);

  await Promise.all([
    axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: 'Test Mode Message',
      ActionPage: 'Test Campaign',
      Status: 'active',
      BeforeData: JSON.stringify(payload),
      AfterData: JSON.stringify(data),
      Comments: "Test message sent successfully",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),

    axios.post(`${apiUrlAdminAcc}/InsertAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: `${totalTested} contacts tested successfully`,
      ActionPage: 'Test Campaign',
      Status: 'active',
      BeforeData: "",
      AfterData: "",
      Comments: "Test message sent successfully",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),
  ]);
}
} catch (err) {
  console.error('Failed to send test message:', err);
  toast.error('Failed to send test message.');

  await Promise.all([
    axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: 'Test Mode Message',
      ActionPage: 'Test Campaign',
      Status: 'Failed',
      BeforeData: JSON.stringify(payload),
      AfterData: null,
      Comments: "Test message sending failed due to an exception",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),

    axios.post(`${apiUrlAdminAcc}/InsertAuditHistory`, {
      UserAccountId: accountId,
      CampaignId: campaignId,
      AdvertiserWorkspaceId: workspaceId,
      Action: `0 tested successfully`,
      ActionPage: 'Test Campaign',
      Status: 'Failed',
      BeforeData: "",
      AfterData: null,
      Comments: "Test message sending failed due to an exception",
      ImpersonatorUserAccountId: isImpersonated ? impersonatorAID : null,
    }),
  ]);
} finally {
      setIsSending(false);
    }
  };

  // ── Close handler — resets everything ─────────────────────────────────────
  const handleClose = () => {
    onOpenChange(false);
    setContacts(['']);
    setView('form');
    setSendResult(null);
  };

  const hasAnyContact = contacts.some((c) => c.trim());
  const hasEmptyField = contacts.some((c) => !c.trim());

  const headerComp = parsedTemplate?.header;

  const bodyComp = parsedTemplate?.body;
  const footerComp = parsedTemplate?.footer;
  const buttonsComp = parsedTemplate?.buttons;

  const failedContacts =
    sendResult?.results.filter((r) => r.status === 'failed') ?? [];
  const skippedContacts =
    sendResult?.results.filter((r) => r.status === 'skipped') ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          view === 'result'
            ? 'sm:max-w-[480px] w-full flex flex-col gap-4 p-6'
            : 'sm:max-w-[860px] w-full max-h-[90vh] flex flex-col overflow-hidden p-2'
        }
      >
        {/* ══════════════════════════════════════════
            FORM VIEW
        ══════════════════════════════════════════ */}
        {view === 'form' && (
          <>
            <div className='flex flex-1 min-h-0 gap-4 px-5 pt-5 pb-2 overflow-hidden'>
              {/* ── Left: Contact inputs ── */}
              <div className='flex flex-col w-1/2 gap-1 p-6 overflow-y-auto border border-gray-300 rounded-xl'>
                <p className='text-xl font-bold text-gray-900'>Test Campaign</p>
                <p className='mb-6 text-sm text-gray-500'>
                  Enter phone numbers to send a test the campaign.
                </p>
                <p className='mb-1 text-sm font-semibold text-gray-800'>
                  Enter Contact Details
                </p>
                <p className='mb-5 text-xs text-gray-400'>
                  Add phone numbers with country codes.
                </p>

                <div className='flex flex-col gap-3 pr-1 overflow-y-auto'>
                  {contacts.map((contact, index) => (
                    <div key={index} className='flex flex-col gap-1'>
                      <div key={index} className='flex items-center gap-2'>
                        <Input
                          type='tel'
                          placeholder='eg, +1 555 123 4567'
                          value={contact}
                          onChange={(e) => handleChange(index, e.target.value)}
                          className='flex-1 h-10 text-sm border border-gray-300 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                        />
                        <button
                          onClick={() => handleRemove(index)}
                          disabled={contacts.length === 1}
                          className='p-1 text-gray-400 transition-colors shrink-0 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed'
                          aria-label='Remove field'
                        >
                          <FaTrash className='w-4 h-4' />
                        </button>
                      </div>
                      {!contact.trim() && (
                        <p className='text-xs text-red-500 ml-1 italic'>
                          This field is required. Please enter a number or remove this field.
                        </p>
                      )}
                    </div>

                  ))}
                </div>

                <p className='mt-2 text-xs text-gray-400'>
                  Note: You can add up to 10 contacts for a test send.
                </p>
                {contacts.length < MAX_CONTACTS && (
                  <div className='flex items-center gap-3 mt-2'>
                    <Button
                      variant='default'
                      size='sm'
                      onClick={handleAddField}
                      className='flex items-center self-start gap-1 mt-5'
                    >
                      <Plus className='w-4 h-4' />
                      Add Another Field
                    </Button>

                    <span className='text-xs text-black-400 font-medium mt-4'>
                      Contact count : {contacts.length}/{MAX_CONTACTS}
                    </span>

                  </div>
                )}

              </div>

              {/* ── Right: Content Preview ── */}
              <div className='flex flex-col w-1/2 p-6 overflow-y-auto border border-gray-300 rounded-xl'>
                <p className='mb-4 text-sm font-semibold text-gray-800'>
                  Content Preview
                </p>
                <div
                  className='relative flex-1 overflow-hidden border border-gray-200 shadow-sm rounded-xl'
                  style={{
                    minHeight: '420px',
                    backgroundImage: `url(${Default_WhatsApp_background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  <div className='absolute inset-0 bg-white/10' />
                  <div className='relative z-10 flex flex-col h-full p-5'>
                    {loadingPreview && (
                      <div className='flex items-center justify-center flex-1'>
                        <p className='text-xs text-gray-400 animate-pulse'>
                          Loading preview...
                        </p>
                      </div>
                    )}

                    {!loadingPreview && noContentSelected && (
                      <div className='flex items-center justify-center flex-1'>
                        <p className='text-xs text-gray-400'>
                          No content to preview yet
                        </p>
                      </div>
                    )}

                    {!loadingPreview && !noContentSelected && (
                      <div
                        className='flex flex-col overflow-hidden bg-white shadow-md rounded-xl'
                        style={{ maxWidth: '88%', alignSelf: 'flex-start' }}
                      >
                        {/* HEADER */}
                        {headerComp && (
                          <>
                            {(!headerComp.format ||
                              headerComp.format?.toUpperCase() === 'TEXT') &&
                              headerComp.text && (
                                <div className='px-4 pt-4 pb-1'>
                                  <p className='text-sm font-bold leading-snug text-gray-900'>
                                    {headerComp.text}
                                  </p>
                                </div>
                              )}
                            {headerComp.format?.toUpperCase() === 'IMAGE' && (
                                              <div className="w-full">
                  <img
                    src={getBase64Src(base64, "image") || ""}
                    alt="Header Image"
                    className="w-full h-40 mt-2 object-cover mx-auto rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                    </div>
                            )}
                            {headerComp.format?.toUpperCase() === 'VIDEO' && (
                <div className="w-full">
                  <video
                    controls
                    className="w-full h-[200px] object-cover mx-auto rounded-lg"
                    src={getBase64Src(base64, "video") || ""}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
                            )}
                            {headerComp.format?.toUpperCase() ===
                              'DOCUMENT' && (
                <div className="w-full">
                  <div className="mb-2 p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                    <div className="w-6 h-6 text-red-500 text-2xl">📄</div>
                    <div>
                      <div className="text-sm font-medium">
                        {mediaConfig?.file_name || "Document.pdf"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {mediaConfig?.file_length ? `${(parseInt(mediaConfig.file_length) / 1024).toFixed(1)} KB` : "PDF Document"}
                      </div>
                    </div>
                  </div>
                  <embed
                    src={getBase64Src(base64, "document") || ""}
                    type="application/pdf"
                    width="100%"
                    height="200px"
                    className="rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                              )}
                          </>
                        )}

                        {/* BODY */}
                        {bodyComp?.text && (
                          <div
                            className='px-4 py-3 text-sm leading-relaxed text-gray-700 break-words whitespace-pre-wrap'
                            dangerouslySetInnerHTML={{
                              __html: renderFormattedText(bodyComp.text),
                            }}
                          />
                        )}

                        {/* FOOTER */}
                        {footerComp?.text && (
                          <div className='px-4 pb-3'>
                            <p className='pt-2 text-xs text-gray-400 border-t border-gray-100'>
                              {footerComp.text}
                            </p>
                          </div>
                        )}

                        {/* BUTTONS */}
                        {buttonsComp?.buttons &&
                          buttonsComp.buttons.length > 0 && (
                            <div>
                              {buttonsComp.buttons.map((btn, idx) => (
                                <React.Fragment key={idx}>
                                  <div className='border-t border-gray-200' />
                                  <p className='px-4 py-2 text-sm font-semibold text-center text-blue-500'>
                                    {btn.text}
                                  </p>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Footer actions ── */}
            <div className='flex justify-end gap-4 px-5 mb-4'>
              <Button
                variant='outline'
                className='mt-1 w-36'
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                className='mt-1 w-36'
                disabled={!hasAnyContact || hasEmptyField || isSending || !templateDetails}
                onClick={handleSend}
              >
                {isSending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            RESULT VIEW
        ══════════════════════════════════════════ */}
        {view === 'result' && sendResult && (
          <>
            {/* Title */}
            <div>
              <p className='text-lg font-bold text-gray-900'>
                {sendResult.status === 'success'
                  ? 'Test Message Sent'
                  : 'Test Message Partially Sent'}
              </p>
              <p className='mt-1 text-sm text-gray-500'>
                {sendResult.status === 'success'
                  ? 'All messages were delivered successfully.'
                  : 'Some messages could not be delivered.'}
              </p>
            </div>

            {/* Summary Cards */}
            <div className='grid grid-cols-2 gap-3'>
              <div className='flex flex-col items-center justify-center gap-1 py-4 border border-gray-200 rounded-xl'>
                <p className='text-2xl font-bold text-gray-800'>
                  {sendResult.total}
                </p>
                <p className='text-xs text-gray-500'>Total</p>
              </div>
              <div className='flex flex-col items-center justify-center gap-1 py-4 border border-green-200 bg-green-50 rounded-xl'>
                <p className='text-2xl font-bold text-green-600'>
                  {sendResult.success_count}
                </p>
                <p className='text-xs text-green-500'>Successful</p>
              </div>
              <div className='flex flex-col items-center justify-center gap-1 py-4 border border-red-200 bg-red-50 rounded-xl'>
                <p className='text-2xl font-bold text-red-500'>
                  {sendResult.failure_count}
                </p>
                <p className='text-xs text-red-400'>Failed</p>
              </div>
              <div className='flex flex-col items-center justify-center gap-1 py-4 border border-yellow-200 bg-yellow-50 rounded-xl'>
                <p className='text-2xl font-bold text-yellow-500'>
                  {sendResult.skipped_count}
                </p>
                <p className='text-xs text-yellow-400'>Skipped</p>
              </div>
            </div>

            {/* Failed Contacts */}
            {failedContacts.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <XCircle className='w-4 h-4 text-red-500' />
                  <p className='text-sm font-semibold text-red-500'>
                    Failed Contacts
                  </p>
                </div>
                <div className='flex flex-col gap-2 overflow-y-auto max-h-36'>
                  {failedContacts.map((item, idx) => (
                    <div
                      key={idx}
                      className='flex flex-col px-3 py-2 border border-red-100 rounded-lg bg-red-50'
                    >
                      <p className='text-sm font-medium text-gray-800'>
                        {item.msisdn}
                      </p>
                      {item.remarks && (
                        <p className='mt-0.5 text-xs text-red-400'>
                          {item.remarks}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skipped Contacts */}
            {skippedContacts.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <SkipForward className='w-4 h-4 text-yellow-500' />
                  <p className='text-sm font-semibold text-yellow-500'>
                    Skipped Contacts
                  </p>
                </div>
                <div className='flex flex-col gap-2 overflow-y-auto max-h-36'>
                  {skippedContacts.map((item, idx) => (
                    <div
                      key={idx}
                      className='flex flex-col px-3 py-2 border border-yellow-100 rounded-lg bg-yellow-50'
                    >
                      <p className='text-sm font-medium text-gray-800'>
                        {item.msisdn}
                      </p>
                      {item.remarks && (
                        <p className='mt-0.5 text-xs text-yellow-500'>
                          {item.remarks}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className='flex justify-end mt-2'>
              <Button className='w-32' onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TestModeDialog;
